// One tasting a friend chose to share ("Kim proefde Barolo 2019"), with the line they added if they
// added one and the reactions cards already have. A tap opens the wine's page when the wine has one
// anybody can open, else the person. Used by Home's "Vrienden proefden" and the Samen activity list.
// A share holds a wine, a place on the scale and the author's own line, so everything here is
// exactly that: nothing is derived, and no line is invented when there is none.
import type { ReactionKind, TastingShareView } from "@proef/contract";
import { REACTION_KINDS, shareWineTitle } from "@proef/domain";
import { mk, useT } from "../lib/i18n";
import { navigate } from "../lib/router";
import { catalogueWinePath } from "../lib/wineIdentity";
import {
  Avatar,
  Body,
  Caption,
  Card,
  Chip,
  Column,
  ErrorBanner,
  Icon,
  Pressable,
  Row,
} from "../ui";
import { initialsOf } from "./PersonRow";

export interface SharedTastingCardProps {
  share: TastingShareView;
  /** Called with the reaction picked, or null to take back the one given. */
  onReact: (kind: ReactionKind | null) => void;
  /** Opens the person, for a wine with no page of its own. */
  onOpenPerson: (userId: string) => void;
  /** A reaction is on its way; the chips wait. */
  reacting?: boolean | undefined;
  error?: string | undefined;
  testId?: string | undefined;
}

export function SharedTastingCard({
  share,
  onReact,
  onOpenPerson,
  reacting = false,
  error,
  testId,
}: SharedTastingCardProps): React.JSX.Element {
  const { t } = useT();
  const { user, wine } = share;
  const title = shareWineTitle(wine.name, wine.vintage);
  const winePath = wine.wineId === null ? undefined : catalogueWinePath(wine.wineId);

  return (
    <Card data-testid={testId} gap="3">
      <Pressable
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap="3"
        width="100%"
        minHeight="var(--uiTarget)"
        aria-label={
          winePath === undefined
            ? t("social.shared.openPerson", { name: user.displayName })
            : t("social.shared.openWine", { wine: title })
        }
        data-testid={testId === undefined ? undefined : `${testId}-open`}
        onPress={() => {
          if (winePath === undefined) onOpenPerson(user.id);
          else navigate(winePath);
        }}
      >
        <Avatar
          initials={initialsOf(user.displayName)}
          src={user.avatarUrl ?? undefined}
          size="row"
        />
        <Column flex={1} minWidth={0} gap="1" alignItems="flex-start">
          <Body weight="medium" textAlign="left">
            {t("social.shared.row", { name: user.displayName, wine: title })}
          </Body>
          {share.quote !== undefined && (
            <Body
              tone="dim"
              textAlign="left"
              data-testid={testId === undefined ? undefined : `${testId}-quote`}
            >
              {t("social.shared.quote", { quote: share.quote })}
            </Body>
          )}
          <Caption>{t(mk(`note.quality.${share.band}`))}</Caption>
        </Column>
        <Icon name="chevron-right" size={18} tone="textFaint" />
      </Pressable>
      <Row
        gap="2"
        wrap
        role="group"
        aria-label={t("social.shared.react", { name: user.displayName, wine: title })}
      >
        {REACTION_KINDS.map((kind) => (
          <Chip
            key={kind}
            label={t(mk(`social.reaction.${kind}`))}
            active={share.myReaction === kind}
            disabled={reacting}
            count={share.reactions[kind]}
            onPress={() => {
              onReact(share.myReaction === kind ? null : kind);
            }}
          />
        ))}
      </Row>
      {error !== undefined && <ErrorBanner message={error} />}
    </Card>
  );
}
